import { Chip } from '@/components/Chip';
import { Tone } from '@gokrew/models/ui.types';
import type { CompanyTaskActivityLog } from '@gokrew/models/tasks';
import TableEmptyState from '@/components/TableEmptyState/LibsTableEmptyState';
import {
  ClipboardListIcon,
  PlusCircleIcon,
  PencilIcon,
  ArrowRightLeftIcon,
  SendIcon,
  CheckCircleIcon,
  RotateCcwIcon,
  UserPlusIcon,
  UserMinusIcon,
  MessageSquareIcon,
  Trash2Icon,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { formatDate } from '@/utility/utility';
import { cn } from '@/lib/utils';
import styles from './TaskActivityTimeline.module.css';

const ACTIVITY_CONFIG: Record<
  string,
  { labelKey: string; descriptionKey: string; tone: Tone; icon: LucideIcon }
> = {
  task_created: {
    labelKey: 'tasks.activity.types.task_created',
    descriptionKey: 'tasks.activity.descriptions.task_created',
    tone: Tone.PRIMARY,
    icon: PlusCircleIcon,
  },
  task_updated: {
    labelKey: 'tasks.activity.types.task_updated',
    descriptionKey: 'tasks.activity.descriptions.task_updated',
    tone: Tone.SOFT_BLUE,
    icon: PencilIcon,
  },
  occurrence_created: {
    labelKey: 'tasks.activity.types.occurrence_created',
    descriptionKey: 'tasks.activity.descriptions.occurrence_created',
    tone: Tone.PRIMARY,
    icon: ClipboardListIcon,
  },
  status_changed: {
    labelKey: 'tasks.activity.types.status_changed',
    descriptionKey: 'tasks.activity.descriptions.status_changed',
    tone: Tone.SOFT_BLUE,
    icon: ArrowRightLeftIcon,
  },
  submitted: {
    labelKey: 'tasks.activity.types.submitted',
    descriptionKey: 'tasks.activity.descriptions.submitted',
    tone: Tone.WARNING,
    icon: SendIcon,
  },
  approved: {
    labelKey: 'tasks.activity.types.approved',
    descriptionKey: 'tasks.activity.descriptions.approved',
    tone: Tone.SUCCESS,
    icon: CheckCircleIcon,
  },
  reopened: {
    labelKey: 'tasks.activity.types.reopened',
    descriptionKey: 'tasks.activity.descriptions.reopened',
    tone: Tone.ERROR,
    icon: RotateCcwIcon,
  },
  assignee_added: {
    labelKey: 'tasks.activity.types.assignee_added',
    descriptionKey: 'tasks.activity.descriptions.assignee_added',
    tone: Tone.ACCENT,
    icon: UserPlusIcon,
  },
  assignee_removed: {
    labelKey: 'tasks.activity.types.assignee_removed',
    descriptionKey: 'tasks.activity.descriptions.assignee_removed',
    tone: Tone.ACCENT,
    icon: UserMinusIcon,
  },
  message_posted: {
    labelKey: 'tasks.activity.types.message_posted',
    descriptionKey: 'tasks.activity.descriptions.message_posted',
    tone: Tone.SECONDARY,
    icon: MessageSquareIcon,
  },
  message_updated: {
    labelKey: 'tasks.activity.types.message_updated',
    descriptionKey: 'tasks.activity.descriptions.message_updated',
    tone: Tone.SECONDARY,
    icon: MessageSquareIcon,
  },
  message_deleted: {
    labelKey: 'tasks.activity.types.message_deleted',
    descriptionKey: 'tasks.activity.descriptions.message_deleted',
    tone: Tone.SECONDARY,
    icon: Trash2Icon,
  },
};

const DEFAULT_CONFIG = {
  labelKey: '',
  descriptionKey: '',
  tone: Tone.SECONDARY,
  icon: ClipboardListIcon,
};

const ICON_TONE_CLASSES: Record<Tone, string> = {
  [Tone.PRIMARY]: 'bg-blue-50 text-blue-600',
  [Tone.SECONDARY]: 'bg-gray-100 text-gray-500',
  [Tone.SUCCESS]: 'bg-green-50 text-green-600',
  [Tone.WARNING]: 'bg-amber-50 text-amber-600',
  [Tone.ERROR]: 'bg-red-50 text-red-600',
  [Tone.NEUTRAL]: 'bg-gray-100 text-gray-500',
  [Tone.ACCENT]: 'bg-violet-50 text-violet-600',
  [Tone.SOFT_BLUE]: `${styles.softBlueIcon} text-blue-700`,
  [Tone.SOFT_GRAY]: 'bg-gray-100 text-gray-500',
};

interface TaskActivityTimelineProps {
  activities: CompanyTaskActivityLog[];
  className?: string;
  /**
   * A full sentence for a line the system wrote (e.g. "Reassigned to Bob:
   * manager changed"), shown instead of "<who> <did what>". Return nothing
   * to keep the default.
   */
  describe?: (activity: CompanyTaskActivityLog) => string | null | undefined;
}

interface ActivityGroup {
  dateKey: string;
  dateLabel: string;
  items: CompanyTaskActivityLog[];
}

/** "requiresApproval" -> "Requires Approval" */
function humanizeFieldName(field: string): string {
  const spaced = field.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function groupByDate(
  activities: CompanyTaskActivityLog[],
  locale: string
): ActivityGroup[] {
  const groupsByKey = new Map<string, ActivityGroup>();
  const order: string[] = [];

  activities.forEach((act) => {
    const dateKey = formatDate(act.createdAt, { format: 'YYYY-MM-DD' });
    const existing = groupsByKey.get(dateKey);
    if (existing) {
      existing.items.push(act);
      return;
    }

    const dateLabel = formatDate(act.createdAt, {
      format: 'ddd, D MMM YYYY',
      locale,
    });
    groupsByKey.set(dateKey, { dateKey, dateLabel, items: [act] });
    order.push(dateKey);
  });

  return order.map((dateKey) => groupsByKey.get(dateKey)!);
}

export function TaskActivityTimeline({
  activities,
  className,
  describe,
}: TaskActivityTimelineProps) {
  const { t, i18n } = useTranslation(TranslationNS.LibsCommon);

  if (activities.length === 0) {
    return (
      <div
        className={cn('rounded-lg border border-gray-200 bg-white', className)}
      >
        <TableEmptyState
          Icon={ClipboardListIcon}
          title={t('tasks.activity.empty.title')}
          message={t('tasks.activity.empty.message')}
        />
      </div>
    );
  }

  const groups = groupByDate(activities, i18n.language);

  return (
    <div
      className={cn(
        'rounded-lg border border-gray-200 bg-white p-3 max-h-[470px] overflow-y-auto',
        className
      )}
    >
      <div className="space-y-4">
        {groups.map((group) => (
          <div key={group.dateKey}>
            <p className="mb-2 px-1 text-xs font-semibold text-gray-500">
              {group.dateLabel}
            </p>

            <div className="space-y-2">
              {group.items.map((act) => {
                const name = act.createdByUser
                  ? `${act.createdByUser.firstName} ${act.createdByUser.lastName}`
                  : t('tasks.activity.system');

                const config = ACTIVITY_CONFIG[act.logType] ?? DEFAULT_CONFIG;
                const Icon = config.icon;
                const badgeLabel = config.labelKey
                  ? t(config.labelKey)
                  : act.logType.replace(/_/g, ' ');
                const description = config.descriptionKey
                  ? t(config.descriptionKey)
                  : badgeLabel;
                const sentence = describe?.(act);
                const changedFields = Array.isArray(act.metadata?.changed)
                  ? (act.metadata.changed as string[])
                  : [];

                return (
                  <div
                    key={act.id}
                    className="rounded-lg border border-gray-200 bg-white p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-3">
                        <span
                          className={cn(
                            'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md',
                            ICON_TONE_CLASSES[config.tone]
                          )}
                        >
                          <Icon size={15} />
                        </span>

                        <div className="min-w-0">
                          {sentence ? (
                            <p className="text-sm text-gray-700">{sentence}</p>
                          ) : (
                            <p className="text-sm text-gray-700">
                              <span className="font-semibold text-gray-900">
                                {name}
                              </span>{' '}
                              {description}
                            </p>
                          )}

                          {act.fromValue && act.toValue && (
                            <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                              <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium text-gray-600">
                                {act.fromValue.replace(/_/g, ' ')}
                              </span>
                              <span className="text-gray-400">→</span>
                              <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium text-gray-600">
                                {act.toValue.replace(/_/g, ' ')}
                              </span>
                            </p>
                          )}

                          {changedFields.length > 0 && (
                            <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-gray-500">
                              {changedFields.map((field) => (
                                <span
                                  key={field}
                                  className="rounded bg-gray-100 px-1.5 py-0.5 font-medium text-gray-600"
                                >
                                  {humanizeFieldName(field)}
                                </span>
                              ))}
                            </p>
                          )}

                          <p className="mt-1.5 text-xs text-gray-400">
                            {t('tasks.activity.on', {
                              date: formatDate(act.createdAt, {
                                format: 'monthDayTime',
                                locale: i18n.language,
                              }),
                            })}
                          </p>
                        </div>
                      </div>

                      <Chip
                        type={config.tone}
                        size="sm"
                        className="flex-shrink-0"
                      >
                        {badgeLabel}
                      </Chip>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
